import { useCallback, useState } from "react";
import { RequireRole } from "../../auth/RequireRole";
import { ApiError } from "../../api/client";
import type { AdminSegmentSummary } from "../../api/types";
import { Field } from "../../components/Field";
import { createSegment } from "./api";
import { HIERARCHY_LEVEL_OPTIONS } from "./hierarchyLevels";

const EMPTY_CREATE_FORM = { nombre: "", nivelJerarquico: [] as string[], industria: "" };

type SubmitState = { status: "idle" } | { status: "submitting" } | { status: "error"; message: string };

interface SegmentCreateFormProps {
  /** Called once after a successful create — lets the caller refresh the segments list. */
  onCreated?: (result: AdminSegmentSummary) => void;
}

/**
 * Create-segment form (panel-segment-crud). Mirrors `TemplateCreateForm`'s exact contract:
 * self-contained (own state, own `RequireRole` gate, own pending state), never resets the
 * typed input on error — only on success. Segments were read-only in v1
 * (`SegmentsScreen`/`SegmentDetailScreen` used to document that decision explicitly); this
 * form is the write side that closes that gap.
 */
export function SegmentCreateForm({ onCreated }: SegmentCreateFormProps) {
  return (
    <RequireRole role="Operador">
      <SegmentCreateFormContent onCreated={onCreated} />
    </RequireRole>
  );
}

function SegmentCreateFormContent({ onCreated }: SegmentCreateFormProps) {
  const [form, setForm] = useState(EMPTY_CREATE_FORM);
  const [submitState, setSubmitState] = useState<SubmitState>({ status: "idle" });

  const toggleNivel = useCallback((value: string) => {
    setForm((prev) => ({
      ...prev,
      nivelJerarquico: prev.nivelJerarquico.includes(value)
        ? prev.nivelJerarquico.filter((v) => v !== value)
        : [...prev.nivelJerarquico, value],
    }));
  }, []);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      // Guards a duplicate submission even if the disabled button somehow
      // still dispatched a second click (defense in depth, same pattern
      // `TemplateCreateForm` already uses).
      if (submitState.status === "submitting") return;
      setSubmitState({ status: "submitting" });
      try {
        const result = await createSegment({
          nombre: form.nombre,
          criterio: {
            nivelJerarquico: form.nivelJerarquico.length > 0 ? form.nivelJerarquico : null,
            industria: form.industria.trim() === "" ? null : form.industria,
          },
        });
        setForm(EMPTY_CREATE_FORM);
        setSubmitState({ status: "idle" });
        onCreated?.(result);
      } catch (error) {
        // El input tipeado NUNCA se descarta en error — solo en exito (mismo
        // contrato que TemplateCreateForm).
        setSubmitState({
          status: "error",
          message: error instanceof ApiError ? error.message : "No se pudo crear el segmento.",
        });
      }
    },
    [form, submitState.status, onCreated],
  );

  const isSubmitting = submitState.status === "submitting";

  return (
    <form onSubmit={(event) => void handleSubmit(event)} aria-label="Crear segmento">
      <h2>Crear segmento</h2>

      <Field label="Nombre" htmlFor="segment-nombre">
        <input
          id="segment-nombre"
          className="v2-u-target-min"
          value={form.nombre}
          onChange={(e) => setForm({ ...form, nombre: e.target.value })}
          disabled={isSubmitting}
          required
        />
      </Field>

      {/* User follow-up 2026-09-16: replaces the always-open 5-checkbox
          fieldset with a collapsed-by-default disclosure — same accessible,
          keyboard-native <details>/<summary> technique already used for
          TemplateDetailScreen's rutaBlob and OperationalHealthScreen's
          blockedBy. Keeps multi-select (a native <select> would lose it). */}
      <details className="v2-u-checkbox-dropdown">
        <summary className="v2-u-target-min">
          Nivel jerárquico{form.nivelJerarquico.length > 0 ? ` (${form.nivelJerarquico.length})` : ""}
        </summary>
        <fieldset>
          <legend>Nivel jerárquico</legend>
          {HIERARCHY_LEVEL_OPTIONS.map((option) => (
            <label key={option.value} className="v2-u-target-min">
              <input
                type="checkbox"
                checked={form.nivelJerarquico.includes(option.value)}
                onChange={() => toggleNivel(option.value)}
                disabled={isSubmitting}
              />
              {option.label}
            </label>
          ))}
        </fieldset>
      </details>

      <Field label="Industria" htmlFor="segment-industria">
        <input
          id="segment-industria"
          className="v2-u-target-min"
          value={form.industria}
          onChange={(e) => setForm({ ...form, industria: e.target.value })}
          disabled={isSubmitting}
        />
      </Field>

      {submitState.status === "error" && <p role="alert">{submitState.message}</p>}

      <button type="submit" className="v2-u-target-min" disabled={isSubmitting} aria-busy={isSubmitting}>
        {isSubmitting ? "Creando…" : "Crear segmento"}
      </button>
    </form>
  );
}
